import { Fragment, useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { ScreenSpinner } from "@/components";
import { CareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { isCurrentProduct } from "@/domains/careplan/factories";
import { useMarkCareplanAsRead } from "@/domains/careplan/mutations/use-mark-careplan-as-read";
import { useCareplan } from "@/domains/careplan/queries/use-careplan";
import { useIsCareplanCartEmpty } from "@/domains/careplan/queries/use-is-careplan-cart-empty";
import { useMessages } from "@/domains/messages/queries/use-messages";
import { useProfile } from "@/domains/profile/queries/use-profile";
import { ProgressTeaserCard } from "@/domains/progress/components";
import { selectCareplanProgressTeaserBody } from "@/domains/progress/utils/progress-entry-points";
import { Banner } from "@/routes/_private/(with-footer)/careplan/-components/banner";
import { PreviousCareplanProduct } from "@/routes/_private/(with-footer)/careplan/-components/previous-careplan-product";
import { isCartProcessing, useCartStore } from "@/stores";
import { Button, icons } from "@/ui";
import {
  CareplanProduct,
  CareplanSummaryCard,
  ImageLinkList,
  OngoingSupportCard,
  StickyCheckoutBar,
  YourCareplanHeader,
  YourCareplanHeaderMobile,
} from "./-components";

const Careplan = () => {
  const cartCreationFailed = useCartStore((state) => {
    return state.cartCreationFailed;
  });
  const openCart = useCartStore((state) => {
    return state.openCart;
  });
  const isCartProcessingNow = useCartStore(isCartProcessing);
  const isCartEmpty = useIsCareplanCartEmpty();
  const [firstProductCard, setFirstProductCard] = useState<HTMLElement | null>(null);
  const [firstCurrentProductCard, setFirstCurrentProductCard] = useState<HTMLElement | null>(null);
  const setFirstAndFirstCurrentProductCard = useCallback((node: HTMLElement | null) => {
    setFirstProductCard(node);
    setFirstCurrentProductCard(node);
  }, []);
  const { data: profile } = useProfile();
  const careplanQuery = useCareplan();
  const messagesQuery = useMessages();

  useMarkCareplanAsRead({
    isRead: careplanQuery.careplan?.isRead,
    careplanId: careplanQuery.careplan?.id,
  });

  const progressTeaserBody = selectCareplanProgressTeaserBody(careplanQuery.careplan);

  const { careplan } = careplanQuery;
  const showStickyCheckout =
    !!careplan && !careplan.subscription && !cartCreationFailed && !isCartProcessingNow;
  const firstCurrentProductId = careplan?.productsWithHistory.find(isCurrentProduct)?.id;

  const getProductCardRef = (productId: number, isFirstProduct: boolean) => {
    const isFirstCurrentProduct = productId === firstCurrentProductId;

    if (isFirstProduct && isFirstCurrentProduct) {
      return setFirstAndFirstCurrentProductCard;
    }
    if (isFirstProduct) {
      return setFirstProductCard;
    }
    if (isFirstCurrentProduct) {
      return setFirstCurrentProductCard;
    }

    return undefined;
  };

  /*
    Refetching a query that never held data resets its status to pending and clears its error, so
    `isError` drops back to the spinner on every remount and window focus. `errorUpdatedAt` is the
    one failure marker TanStack keeps across that reset.
  */
  const hasCareplanFailed = careplanQuery.errorUpdatedAt > 0 && !careplanQuery.careplan;

  if (!profile || (careplanQuery.isLoading && !hasCareplanFailed) || !messagesQuery.data) {
    return <ScreenSpinner />;
  }

  return (
    <>
      <YourCareplanHeaderMobile
        className="mb-6 sm:hidden"
        latestMessage={{
          message: messagesQuery.data.latestSystemMessage,
          truncate: !hasCareplanFailed,
        }}
        title={
          hasCareplanFailed ? (
            "Our Clinical Team Is Reviewing Your Plan"
          ) : (
            <>
              <div className="font-extrabold">{`${profile.firstName}'s`}</div>
              <div className="font-medium">Care Plan</div>
            </>
          )
        }
      />

      <div className="flex w-full max-w-4xl grow flex-col gap-6 self-center px-4 pt-0 pb-16 sm:gap-8 sm:px-6 sm:pt-16">
        <YourCareplanHeader
          className="hidden sm:flex"
          latestMessage={{
            message: messagesQuery.data.latestSystemMessage,
            truncate: !hasCareplanFailed,
          }}
          title={
            hasCareplanFailed
              ? "Our Clinical Team Is Reviewing Your Plan"
              : `${profile.firstName}'s Care Plan`
          }
        />

        {careplan ? (
          <CareplanContext.Provider value={careplan}>
            <CareplanSummaryCard detailsTarget={firstCurrentProductCard} />
          </CareplanContext.Provider>
        ) : null}

        {progressTeaserBody === null ? null : <ProgressTeaserCard body={progressTeaserBody} />}

        {careplanQuery.careplan ? (
          <CareplanContext.Provider value={careplanQuery.careplan}>
            {careplanQuery.careplan.productsWithHistory.map((product, index) => {
              const isFirstProduct = index === 0;
              const productCardRef = getProductCardRef(product.id, isFirstProduct);

              return (
                <Fragment key={product.id}>
                  {product.isPrevious ? (
                    <PreviousCareplanProduct product={product} ref={productCardRef} />
                  ) : (
                    <CareplanProduct product={product} ref={productCardRef} />
                  )}
                  {isFirstProduct ? (
                    <Banner
                      icon={icons.HydrateIcon}
                      text="Hydrate well for gentler, consistent cannabinoid effects."
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </CareplanContext.Provider>
        ) : null}

        {!hasCareplanFailed && <OngoingSupportCard />}
        <Banner text="While cannabinoids often provide substantial and immediate relief, best results are typically achieved after 60-90 days of guided use." />
        {!!careplanQuery.careplan && !cartCreationFailed && (
          <Button className="w-full" onClick={openCart} size="lg">
            <span>View Cart & Check Out</span>
            <icons.ArrowRight className="size-5" />
          </Button>
        )}
        <ImageLinkList />
      </div>
      {showStickyCheckout ? (
        <CareplanContext.Provider value={careplan}>
          <StickyCheckoutBar isEmpty={isCartEmpty} revealTarget={firstProductCard} />
        </CareplanContext.Provider>
      ) : null}
    </>
  );
};

export const Route = createFileRoute("/_private/(with-footer)/careplan/")({
  component: Careplan,
});
